/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import { useRef, useEffect, useState } from 'react';
import type {
  GeoWebPluginInstance,
  PluginManager,
  PluginInternalState,
} from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import { AlertBanner } from '@opengeoweb/shared';
import { usePluginReduxBridge } from './PluginBridge';
import { getPluginKey, usePlugin } from './PluginContext';
import { useRegisterDebugManager } from './PluginDebug/PluginDebugContext';
import { usePluginsTranslation } from '../utils/i18n';

const EMPTY_PLUGIN_STATE: PluginInternalState = {};

export function usePluginVersions(pluginId: string): {
  versions: string[];
  defaultVersion: string | undefined;
} {
  const { pluginCatalog } = usePlugin();
  const pluginMeta = pluginCatalog?.[pluginId];
  const versions = pluginMeta?.versions ?? [];
  const defaultVersion = pluginMeta?.version;
  return { versions, defaultVersion };
}

interface PluginComponentProps {
  sourceId: string;
  pluginId: string;
  pluginState?: PluginInternalState;
  onGeoWebPluginStateChange?: (state: PluginInternalState) => void;
  version?: string;
  applicationConfig?: ConfigType;
  hostPanelId?: string;
}
export const PluginComponent: React.FC<PluginComponentProps> = ({
  sourceId,
  pluginId,
  pluginState: initialPluginState = EMPTY_PLUGIN_STATE,
  onGeoWebPluginStateChange,
  version: initialVersion,
  applicationConfig,
  hostPanelId,
}): React.ReactElement => {
  const { t } = usePluginsTranslation();
  const pluginRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<GeoWebPluginInstance | null>(null);
  const pluginBridgeRef = useRef<PluginManager | null>(null);
  const isExternalUpdateRef = useRef(false);
  const [error, setError] = useState<Error | null>(null);
  const {
    loadedPlugins,
    loadPlugin,
    createPluginBridge,
    registerInstance,
    unregisterInstance,
    failedPlugins,
  } = usePlugin();

  const { defaultVersion } = usePluginVersions(pluginId);

  pluginBridgeRef.current ??= createPluginBridge();
  const selectedVersion = initialVersion ?? defaultVersion;

  const pluginKey = getPluginKey(pluginId, selectedVersion);
  const plugin = loadedPlugins[pluginKey];

  useEffect(() => {
    if (!plugin) {
      void loadPlugin(pluginId, selectedVersion);
    }
  }, [plugin, loadPlugin, pluginId, selectedVersion]);

  usePluginReduxBridge({
    pluginBridge: pluginBridgeRef.current,
    sourceId,
    pluginId,
    scopes: plugin?.scopes,
    applicationConfig,
    hostPanelId,
  });

  // Expose this plugin's bridge to the debug layer (no-op when the
  // PluginDebugProvider is not mounted, so this stays free in production).
  useRegisterDebugManager(sourceId, pluginBridgeRef.current);

  useEffect(() => {
    if (
      initialPluginState &&
      instanceRef.current?.getPluginState?.() !== initialPluginState
    ) {
      isExternalUpdateRef.current = true;
      instanceRef.current?.setPluginState?.(initialPluginState);
      isExternalUpdateRef.current = false;
    }
  }, [initialPluginState]);

  useEffect(() => {
    if (!pluginRef.current || !plugin || !pluginBridgeRef.current) {
      return (): void => {};
    }
    let instance: GeoWebPluginInstance | null = null;
    try {
      if (error) {
        setError(null);
      }
      instance = plugin.createInstance(
        pluginRef.current,
      ) as GeoWebPluginInstance;
      pluginBridgeRef.current.registerPlugin(instance);
      instanceRef.current = instance;

      // Register the instance with the plugin context
      registerInstance(sourceId, instance);

      if (initialPluginState) {
        isExternalUpdateRef.current = true;
        instance.setPluginState?.(initialPluginState);
        isExternalUpdateRef.current = false;
      }
    } catch (error) {
      setError(error as Error);
    }

    const handlePluginStateChange = (state: PluginInternalState): void => {
      if (!isExternalUpdateRef.current && state !== initialPluginState) {
        onGeoWebPluginStateChange?.(state);
      }
    };

    const unsubscribePluginStateChange =
      instance?.onPluginStateChange?.(handlePluginStateChange) ?? null;

    return (): void => {
      if (instanceRef.current && pluginBridgeRef.current) {
        unsubscribePluginStateChange?.();
        pluginBridgeRef.current.unregisterPlugin(instanceRef.current);
        // Unregister from context
        unregisterInstance(sourceId);
        instanceRef.current = null;
      }
    };
    // initial plugin state seems to cause reenders here (see plugin state story)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin]);

  const loadError = pluginKey ? failedPlugins[pluginKey] : undefined;

  if (loadError) {
    return (
      <AlertBanner
        severity="error"
        title={t('failed-to-load-plugin', {
          pluginKey,
          reason: loadError,
        })}
      />
    );
  }

  return (
    <>
      {!!error && <AlertBanner severity="error" title={error.message} />}
      {plugin ? (
        <Box
          ref={pluginRef}
          sx={{
            width: '100%',
            height: '100%',
            minWidth: 0,
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            '& > *': {
              minWidth: 0,
              maxWidth: '100%',
            },
          }}
        />
      ) : (
        <Box>{t('loading-plugin')}</Box>
      )}
    </>
  );
};
